<template>
  <div
    v-if="currentUser"
    class="py-2"
  >

    <BSidebar
      id="sbASMCMemoHistoryFilters"
      backdrop-variant="dark"
      backdrop
      shadow
      right
    >
      <template #title>
        <i class="fad fa-filter-list" /> Filters
      </template>

      <BContainer>
        <BRow>

          <BCol
            cols="12"
          >
            <BFormGroup
              label="Month"
              label-for="filterMonth"
              label-class="font-weight-bolder"
              class="mb-2"
            >
              <flat-pickr
                id="filterMonth"
                v-model="tableHistory.filter.month"
                class="form-control"
                placeholder="--------- ----"
                :config="flatPickerConfig"
              />
            </BFormGroup>
          </BCol>

        </BRow>
      </BContainer>
    </BSidebar>

    <BRow
      class="gap-y-2"
    >
      <BCol
        cols="12"
      >
        <BFormGroup
          label="Search"
          label-for="filterSearch"
          label-class="font-weight-bolder"
          class="mb-0"
        >
          <BInputGroup>
            <BFormInput
              id="filterSearch"
              v-model="tableHistory.filter.search"
              placeholder="search here"
              autocomplete="off"
              debounce="1000"
              type="text"
            />
            <BInputGroupAppend>
              <BButton
                v-b-toggle.sbASMCMemoHistoryFilters
                variant="primary"
              >
                <i
                  v-if="[settingFilters].some(isTrue => isTrue)"
                  class="fad fa-spinner fa-spin p-0"
                />
                <i
                  v-else
                  class="fad fa-filter-list"
                /> Filters
              </BButton>
            </BInputGroupAppend>
          </BInputGroup>
        </BFormGroup>
      </BCol>
      <BCol>
        <BOverlay
          rounded
          opacity="0.1"
          spinner-small
          class="d-inline"
          spinner-variant="warning"
          :show="exporter.excel.exporting"
          style="margin-bottom: 1.1px;"
        >
          <JsonExcel
            :class="[
              'btn btn-sm btn-primary', {
                'btn-outline-primary disabled': exporter.excel.exporting || !isValidForExport
              }
            ]"
            :fetch="excelProvider"
            :before-generate="onStartExcelExport"
            :before-finish="onFinishExcelExport"
            :name="excelFileName"
            :fields="exporter.excel.fields"
            :escape-csv="exporter.excel.escape"
            :type="exporter.excel.type"
            :header="exporter.excel.header"
            :footer="exporter.excel.footer"
            worksheet="Customer Memos"
          >
            {{ exporter.excel.exporting ? 'Exporting' : 'Export As Excel' }}
          </JsonExcel>
        </BOverlay>
      </BCol>
    </BRow>

    <CustomTable
      ref="tableHistory"
      class="mt-3"
      :provider="tableProvider"
      :fields="tableHistory.fields"
      :filter="tableHistory.filter"
      :filter-on="tableHistory.filterOn"
      :sort-by="tableHistory.sortBy"
      :sort-desc="tableHistory.sortDesc"
      :sort-direction="tableHistory.sortDirection"
      :total-rows="tableHistory.totalRows"
      :current-page="tableHistory.currentPage"
      :per-page="tableHistory.perPage"
      :stacked="tableHistory.stacked"
      :busy="tableHistory.busy"
    >

      <template #cell(assignee)="{ item }">
        <div class="d-flex flex-column gap-1 p-1">
          <div class="font-weight-bolder">
            {{ getFullName(null, null, item) }}
          </div>
          <div style="font-size: 10px;">
            {{ Array.from(item.clusters, cluster => cluster.cluster_name).join(', ') || 'n/a' }}
          </div>
        </div>
      </template>

    </CustomTable>
  </div>
</template>
<script>
import { mapGetters } from 'vuex'

import FPMonthSelect from 'flatpickr/dist/plugins/monthSelect'
import moment from 'moment'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'

import MCustomers from '@/mixins/accounts/customers'
import MMemos from '@/mixins/maintenances/memos'

import { SASMFsoCustomerMemo } from '@/services/asm'

// import { find } from 'lodash'

export default {
  name: 'ASMCustomerMemosSummaries',

  mixins: [
    MxMisc,
    MxFormatter,
    MCustomers,
    MMemos
  ],

  emits: [
    'create:row',
    'edit:row',
    'verify:row',
    'view:row'
  ],

  data () {
    return {
      api: {},
      flatPickerConfig: {
        appendTo: document.body,
        altInput: true,
        allowInput: true,
        disableMobile: true,
        plugins: [
          new FPMonthSelect({
            dateFormat: 'Y-m',
            altFormat: 'F Y'
          })
        ]
      },
      state: {
        busy: false,
        editing: false
      },
      exporter: {
        excel: {
          exporting: false,
          header: [],
          escape: false,
          type: 'xls',
          fields: {
            Assignee: {
              field: '',
              callback: assignee => `${assignee?.first_name} ${assignee?.last_name}`
            },
            Valid: { field: 'documents_assigned_valid_count' },
            Invalid: { field: 'documents_assigned_invalid_count' },
            Closed: { field: 'documents_assigned_closed_count' },
            Total: { field: 'documents_assigned_count' }
          },
          footer: []
        }
      },
      tableHistory: {
        busy: false,
        filter: {
          search: '',
          month: moment().format('YYYY-MM')
        },
        filterOn: [],
        stacked: 'sm',
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-right stacked-full text-md-center' },
          { mobile: 1, key: 'assignee', label: 'Technical Officer', class: 'min-w-md-225px', formatter: assignee => this.getFullName(null, null, assignee), sortable: true },
          { mobile: 2, key: 'documents_assigned_valid_count', label: 'Valid', class: 'text-md-right min-w-md-50px', sortable: true },
          { mobile: 3, key: 'documents_assigned_invalid_count', label: 'Invalid', class: 'text-md-right min-w-md-50px', sortable: true },
          { mobile: 4, key: 'documents_assigned_closed_count', label: 'Closed', class: 'text-md-right min-w-md-50px', sortable: true },
          { mobile: 5, key: 'documents_assigned_count', label: 'Total', class: 'text-md-right min-w-md-50px', sortable: true }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: {
    ...mapGetters({
      currentUser: 'auth/user'
    }),

    excelFileName () {
      const schedule = moment(this.tableHistory.filter.month).format('YYYY-MM')

      return `Memo Summaries ${schedule}`
    },

    isValidForExport () {
      const isDateFromNotEmpty = ![null, '', ' '].includes(this.tableHistory.filter.month)

      return isDateFromNotEmpty && Number(this.tableHistory.totalRows) >= 1
    },

    settingFilters () {
      return false
    }
  },

  mounted () {
    this.api = this.$refs.tableHistory
  },

  methods: {

    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableHistory.busy = true

      const payload = {
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search,
        schedule: filter.month
      }

      return SASMFsoCustomerMemo.reports(payload).then(
        ({ data }) => {
          this.tableHistory.totalRows = data.total_rows
          return data.items
        }
      ).catch(() => {
        this.tableHistory.totalRows = 0
        return []
      }).finally(() => {
        this.tableHistory.busy = false
      })
    },

    searchFilterMemos (search, loading) {
      this.searchMemosProvider(this.tableHistory, search, loading)
    },

    searchFilterCustomers (search, loading) {
      this.searchCustomersProvider(this.tableHistory, search, loading)
    },

    async excelProvider () {
      if (this.exporter.busy || !this.isValidForExport) {
        return null
      }

      this.exporter.busy = this.exporter.excel.exporting = true

      const payload = {
        filter_text: this.tableHistory.filter.search,
        schedule: this.tableHistory.filter.month
      }

      return SASMFsoCustomerMemo.reportExports(payload).then(
        ({ data: { items } }) => {
          if (items.length <= 0) {
            this.swalInvalid(
              'No available data to be Exported'
            )
          }

          return items
        }
      ).catch(() => {
        return []
      }).finally(() => {
        this.exporter.busy = this.exporter.excel.exporting = false
      })
    },

    onStartExcelExport () {
      if (this.exporter.busy || !this.isValidForExport) {
        return null
      }

      this.exporter.excel.header = []
      this.exporter.excel.footer = []
    },

    onFinishExcelExport () {
    }

  }
}
</script>
